import { PageController } from '@client/components/_core/layout/PageController';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { FormNameCrumb } from './FormNameCrumb';

export const Route = createFileRoute('/console/$orgId/configuration/api-integrations/form-designer/$formDefinitionId')({
    staticData: {
        metadata: {
            title: 'Form editor',
        },
        ui: {
            // Pages list / canvas / inspector all size against the viewport, so the global
            // `container mx-auto` layout preference would squeeze the editor into a centred
            // strip. Pinned on the layout route so every route under it is full width, and
            // paired with the switch lock so the toggle leaves the top bar here rather than
            // offering a preference that would break the workspace.
            widthVariant: 'fullWidth',
            disableLayoutSwitch: true,
        },
    },
    /**
     * The trail used to read "Form designer / Form editor / Edit for…" — two
     * synonymous crumbs and a clipped one, none of which named the form. It now
     * ends at the form itself. `context.breadcrumb` takes precedence over
     * `metadata.title`, which stays as the document title.
     */
    beforeLoad(ctx): ShareableRouteContext {
        if (ctx.params.formDefinitionId === 'new') return { breadcrumb: { title: 'New form' } };
        // A live element, not a value resolved once here: the editor's header both
        // shows and renames the schema title, and a crumb pinned in `beforeLoad`
        // would keep the pre-rename name until the author navigated away and back.
        return { breadcrumb: { title: <FormNameCrumb formDefinitionId={ctx.params.formDefinitionId} /> } };
    },
    component: FormDefinitionDetailLayout,
});

function FormDefinitionDetailLayout() {
    return (
        <PageController route={Route}>
            <Outlet />
        </PageController>
    );
}
